// Ensure Buffer is available in all environments
import { Buffer } from "buffer";
/**
 * Convert base64url to hex string (universal)
 */
export function base64UrlToHex(base64url) {
    // Convert base64url to base64
    let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
        base64 += "=";
    }
    return Buffer.from(base64, "base64").toString("hex");
}
/**
 * Convert base64 to hex string (universal)
 */
export function base64ToHex(base64) {
    return Buffer.from(base64, "base64").toString("hex");
}
/**
 * Convert ArrayBuffer to base64url string (universal)
 */
export function arrayBufferToBase64Url(buffer) {
    const bytes = new Uint8Array(buffer);
    const base64 = Buffer.from(bytes).toString("base64");
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
/**
 * Convert base64url to ArrayBuffer (universal)
 */
export function base64ToArrayBuffer(base64) {
    // Handle base64url format
    base64 = base64.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4 !== 0) {
        base64 += "=";
    }
    // Convert to binary string first, then to ArrayBuffer
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}
/**
 * Convert ArrayBuffer to hex string (universal)
 */
export function arrayBufferToHex(buffer) {
    const bytes = new Uint8Array(buffer);
    return Buffer.from(bytes).toString("hex");
}
/**
 * Parse DER-encoded signature to extract r and s values (universal)
 * Always returns hex strings with 0x prefix
 */
export function parseDERSignature(derBytes) {
    let offset = 0;
    // Check SEQUENCE tag
    if (derBytes[offset] !== 0x30) {
        throw new Error("Invalid DER signature: missing SEQUENCE tag");
    }
    offset += 1;
    // Skip total length
    const totalLength = derBytes[offset];
    offset += 1;
    // Parse r
    if (derBytes[offset] !== 0x02) {
        throw new Error("Invalid DER signature: missing INTEGER tag for r");
    }
    offset += 1;
    const rLength = derBytes[offset];
    offset += 1;
    const rBytes = derBytes.slice(offset, offset + rLength);
    offset += rLength;
    // Parse s
    if (derBytes[offset] !== 0x02) {
        throw new Error("Invalid DER signature: missing INTEGER tag for s");
    }
    offset += 1;
    const sLength = derBytes[offset];
    offset += 1;
    const sBytes = derBytes.slice(offset, offset + sLength);
    // Convert to hex strings with 0x prefix
    const r = "0x" + Buffer.from(rBytes).toString("hex");
    const s = "0x" + Buffer.from(sBytes).toString("hex");
    return { r, s };
}
/**
 * Parse DER-encoded public key to extract x and y coordinates (universal)
 * Always returns hex strings with 0x prefix
 */
export function parseDERPublicKey(publicKeyBase64) {
    // Decode base64 to get raw DER bytes
    const derBytes = new Uint8Array(Buffer.from(publicKeyBase64, "base64"));
    // Parse the DER structure
    // This is a simplified parser for ECDSA P-256 public keys in SPKI format
    // Find the public key bit string (starts with 0x04 for uncompressed point)
    let publicKeyStart = -1;
    for (let i = 0; i < derBytes.length - 64; i++) {
        if (derBytes[i] === 0x04 && derBytes.length - i >= 65) {
            publicKeyStart = i + 1; // Skip the 0x04 prefix
            break;
        }
    }
    if (publicKeyStart === -1) {
        throw new Error("Could not find public key coordinates in DER data");
    }
    // Extract x and y coordinates (32 bytes each for P-256)
    const xBytes = derBytes.slice(publicKeyStart, publicKeyStart + 32);
    const yBytes = derBytes.slice(publicKeyStart + 32, publicKeyStart + 64);
    // Convert to hex strings with 0x prefix
    const x = "0x" + Buffer.from(xBytes).toString("hex");
    const y = "0x" + Buffer.from(yBytes).toString("hex");
    return { x, y };
}
/**
 * Extract public key from SPKI format to PEM (universal)
 */
export function extractPublicKeyFromSpki(publicKeyBase64) {
    const spkiBuffer = Buffer.from(publicKeyBase64, "base64");
    const base64Key = spkiBuffer
        .toString("base64")
        .match(/.{1,64}/g)
        ?.join("\n") || "";
    return `-----BEGIN PUBLIC KEY-----\n${base64Key}\n-----END PUBLIC KEY-----`;
}
